import Box from '@/Atoms/Box';
import React, {useState} from 'react';
import {FlatList, Text, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import CCButton from '@/Atoms/CCButton';
import {FontSizes, vs} from '@/Utils/Dimensions';
import Spacer from '@/Atoms/Spacer';
import CCTextInput from '@/Atoms/CCTextInput';
import CCSelect from '@/Atoms/CCSelect';
import CCLoader from '@/Atoms/CCLoader';
import useAlertModalStore from '@/Store/AlertModalStore';
import {styles} from './styles';
import useManageSubscriptionApi from '@/Hooks/SubscriptionApi/useManageSubscriptionApi';
import StaffManagement from '../StaffManagement';

function SubscriptionManagement({userData}) {
  // State variables
  const [mobileNumber, setMobileNumber] = useState('');
  const [planId, setPlanId] = useState('5'); // Default to Aspiring Champions Monthly
  const [customMonths, setCustomMonths] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [bulkMobileNumbers, setBulkMobileNumbers] = useState('');
  const [bulkMode, setBulkMode] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [apiPassword, setApiPassword] = useState(''); // State for API password
  const [activeTab, setActiveTab] = useState('subscription'); // New state for tab navigation

  const openAlertModal = useAlertModalStore(state => state.openModal);

  // Subscription plans data
  const subscriptionPlans = [
    {label: 'Novice (FREE Lifetime)', value: '1'},
    {label: 'Aspiring Learners (YEARLY - ₹2999)', value: '2'},
    {label: 'Aspiring Learners (MONTHLY - ₹349)', value: '3'},
    {label: 'Aspiring Champions (YEARLY - ₹4999)', value: '4'},
    {label: 'Aspiring Champions (MONTHLY - ₹549)', value: '5'},
  ];

  // Handle API responses
  const handleSubscriptionSuccess = responseData => {
    console.log('Subscription Success:', responseData);
    setSuccess(true);

    if (bulkMode) {
      const {totalProcessed, successCount, errorCount} = responseData;
      setSuccessMessage(
        `Processed ${totalProcessed} subscriptions: ${successCount} successful, ${errorCount} failed.`,
      );

      // Reset form after success
      setBulkMobileNumbers('');
    } else {
      setSuccessMessage(
        `Subscription successfully applied to ${mobileNumber}!`,
      );
      // Reset form after success
      setMobileNumber('');
    }

    setCustomMonths('');
    setCustomStartDate('');
  };

  // Handle API errors
  const handleSubscriptionError = error => {
    console.error('Subscription Error:', error);
    if (error.response && error.response.data) {
      setErrorMsg(
        error.response.data.message ||
          'Failed to apply subscription. Please try again.',
      );
    } else {
      setErrorMsg(
        error.message || 'Failed to apply subscription. Please try again.',
      );
    }
    setSuccess(false);
  };

  // API mutation hook
  const {
    mutate: applySubscription,
    isLoading: applyingSubscription,
    error: subscriptionError,
  } = useManageSubscriptionApi(
    handleSubscriptionSuccess,
    handleSubscriptionError,
  );

  // Handlers
  // Helper function to validate date format (YYYY-MM-DD)
  const isValidDateFormat = date => {
    // Check if the date matches YYYY-MM-DD format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      return false;
    }

    // Check if it's a valid date
    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime());
  };

  const handleSubscriptionApply = () => {
    setErrorMsg(null);
    setSuccess(false);

    // Validate API password
    if (!apiPassword) {
      setErrorMsg('API Password is required');
      return;
    }

    // Validate custom months (if provided)
    if (
      customMonths &&
      (!/^\d+$/.test(customMonths) || parseInt(customMonths) <= 0)
    ) {
      setErrorMsg('Custom months must be a positive number');
      return;
    }

    // Validate custom start date (if provided)
    if (customStartDate && !isValidDateFormat(customStartDate)) {
      setErrorMsg('Custom start date must be in YYYY-MM-DD format');
      return;
    }

    if (bulkMode) {
      // Validate bulk input
      if (!bulkMobileNumbers.trim()) {
        setErrorMsg('Please enter at least one mobile number');
        return;
      }

      // Parse mobile numbers from text area
      const mobileNumbers = bulkMobileNumbers
        .split(/[\n,]/) // Split by newlines or commas
        .map(num => num.trim()) // Trim whitespace from each number
        .filter(num => num.length > 0);

      if (mobileNumbers.length === 0) {
        setErrorMsg('Please enter valid mobile numbers');
        return;
      }

      // Confirm bulk operation
      openAlertModal({
        header: 'Confirm Bulk Subscription',
        bodyTxt: `You are about to apply subscription plan ${
          subscriptionPlans.find(p => p.value === planId).label
        } to ${mobileNumbers.length} users. Continue?`,
        buttonText: 'Apply',
        handleConfirmation: () => {
          const payload = {
            mobile_numbers: mobileNumbers, // Array of individual phone numbers
            subscription_plan_id: parseInt(planId),
            bulk: true,
            apiPassword: apiPassword, // Pass the API password
          };

          // Add optional fields if present
          if (customMonths) payload.custom_months = parseInt(customMonths);
          if (customStartDate) payload.custom_start_date = customStartDate;

          applySubscription(payload);
        },
      });
    } else {
      // Single subscription validation
      if (!mobileNumber.trim()) {
        setErrorMsg('Please enter a mobile number');
        return;
      }

      const payload = {
        mobile_number: mobileNumber,
        subscription_plan_id: parseInt(planId),
        apiPassword: apiPassword, // Pass the API password
      };

      // Add optional fields if present
      if (customMonths) payload.custom_months = parseInt(customMonths);
      if (customStartDate) payload.custom_start_date = customStartDate;

      // Add loader text for user feedback
      applySubscription(payload);
    }
  };

  const toggleMode = () => {
    // setBulkMode(!bulkMode);
    setBulkMode(false);
    setErrorMsg(null);
    setSuccess(false);
  };

  // Render the active tab content
  const renderTabContent = () => {
    if (activeTab === 'staff') {
      return <StaffManagement />;
    } else {
      return (
        <>
          {/* Mode Toggle */}
          <Box style={styles.buttonContainer} type="row-center">
            <CCButton onPress={toggleMode} type={bulkMode ? 'outline' : 'fill'}>
              <CCText>Single User</CCText>
            </CCButton>
            <Spacer spacing={10} horizontal />
            {/* <CCButton onPress={toggleMode} type={bulkMode ? 'fill' : 'outline'}>
              <CCText>Bulk Users</CCText>
            </CCButton> */}
          </Box>

          {/* Subscription Form Section */}
          <Box style={styles.section}>
            <CCText style={styles.sectionHeader}>
              {bulkMode
                ? 'Apply Bulk Subscriptions'
                : 'Apply User Subscription'}
            </CCText>
            <Spacer spacing={vs(12)} />

            {bulkMode ? (
              // Bulk mode form
              <Box style={styles.row}>
                <CCTextInput
                  label="Mobile Numbers (one per line or comma-separated)"
                  value={bulkMobileNumbers}
                  onChangeText={setBulkMobileNumbers}
                  multiline={true}
                  numberOfLines={5}
                  style={{...styles.input, minHeight: vs(120)}}
                />
              </Box>
            ) : (
              // Single user form
              <Box style={styles.row}>
                <CCTextInput
                  label="Mobile Number"
                  value={mobileNumber}
                  onChangeText={setMobileNumber}
                  style={styles.input}
                  placeholder="e.g. 919876543210"
                  keyboardType="phone-pad"
                />
              </Box>
            )}

            <Box style={styles.row}>
              <Box style={styles.input}>
                <CCText
                  fontSize={12}
                  color={CCColors.TextColor.Grey}
                  style={{
                    paddingHorizontal: 6,
                    position: 'absolute',
                    zIndex: 1,
                    background: 'white',
                    left: '2%',
                    top: '-6%',
                  }}>
                  Subscription Plan
                </CCText>
                <CCSelect
                  selectedValue={planId}
                  onValueChange={setPlanId}
                  items={subscriptionPlans}
                  style={{
                    backgroundColor: 'white',
                    borderColor: CCColors.BorderBottom,
                  }}
                />
              </Box>
            </Box>

            <Box style={styles.row}>
              <CCTextInput
                label="Custom Months (Optional)"
                value={customMonths}
                onChangeText={text => {
                  // Only allow digits
                  const numericText = text.replace(/[^0-9]/g, '');
                  setCustomMonths(numericText);
                }}
                style={styles.input}
                keyboardType="numeric"
                placeholder="e.g. 3"
              />
              <CCTextInput
                label="Custom Start Date (Optional)"
                value={customStartDate}
                onChangeText={setCustomStartDate}
                style={styles.input}
                placeholder="YYYY-MM-DD"
              />
            </Box>

            {/* API Password Field */}
            <Box style={styles.row}>
              <CCTextInput
                label="API Password (Required)"
                value={apiPassword}
                onChangeText={setApiPassword}
                style={styles.input}
                placeholder="Enter API password"
                secureTextEntry={true}
              />
            </Box>
          </Box>

          {/* Help Text */}
          <Box style={styles.section}>
            <CCText
              style={{fontSize: FontSizes[12], color: CCColors.TextColor.Grey}}>
              Note: Leave Custom fields empty to use default values. Custom
              Start Date must be in YYYY-MM-DD format.
            </CCText>
            {bulkMode && (
              <CCText
                style={{
                  fontSize: FontSizes[12],
                  color: CCColors.TextColor.Grey,
                  marginTop: vs(4),
                }}>
                For bulk mode, enter multiple mobile numbers separated by commas
                or new lines.
              </CCText>
            )}
          </Box>

          {/* Error/Success Messages */}
          {errorMsg && <CCText style={styles.errorText}>{errorMsg}</CCText>}
          {success && (
            <CCText style={styles.successText}>{successMessage}</CCText>
          )}

          {/* Action Button */}
          <Box style={styles.buttonContainer} type="row-center">
            <CCButton onPress={handleSubscriptionApply} type="fill">
              <CCText>
                {bulkMode ? 'Apply Bulk Subscriptions' : 'Apply Subscription'}
              </CCText>
            </CCButton>
          </Box>
        </>
      );
    }
  };

  return (
    <Box flex={1}>
      <CCLoader
        loading={applyingSubscription}
        textToShow={
          bulkMode
            ? 'Applying subscriptions to multiple users...'
            : 'Applying subscription...'
        }
      />

      {/* Tab navigation */}
      <Box style={styles.tabContainer}>
        <Box style={styles.tabContent}>
          <CCButton
            onPress={() => setActiveTab('subscription')}
            type={activeTab === 'subscription' ? 'fill' : 'outline'}
            style={styles.tabButton}>
            <CCText>Subscription Management</CCText>
          </CCButton>
          <Spacer spacing={10} horizontal />
          <CCButton
            onPress={() => setActiveTab('staff')}
            type={activeTab === 'staff' ? 'fill' : 'outline'}
            style={styles.tabButton}>
            <CCText>Staff Management</CCText>
          </CCButton>
        </Box>
      </Box>

      {/* Render the content of the active tab */}
      {renderTabContent()}
    </Box>
  );
}

export default SubscriptionManagement;
